import React, { useEffect, useMemo, useRef, useState } from "react";
import EmailEditor from "react-email-editor";

import MERGE_TAGS from "../../constants/mergeTags";
import { flattenMergeTagsForUnlayer } from "../../utils/flattenMergeTags";
import MergeTagPicker from "../merge-tags/MergeTagPicker";
import "../../styles/EditorWorkspace.css";
import { authFetch } from "../../utils/authFetch";

const UNLAYER_OPTIONS = {
  projectId: 277125,
  appearance: { theme: "modern" },
  features: {
    stockImages: true,
    undoRedo: true,
    preview: true,
  },
};

function EmailComposer({
  recipientEmail,
  recipientName = "",
  mergeData,
  clientId = null,
  leadId = null,
  onClose,
}) {
  const editorRef = useRef(null);
  const toRef = useRef(null);
  const subjectRef = useRef(null);

  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [toEmail, setToEmail] = useState(recipientEmail || "");
  const [subject, setSubject] = useState("Message from AgentBook");
  const [showToTags, setShowToTags] = useState(false);
  const [showSubjectTags, setShowSubjectTags] = useState(false);
  const [groups, setGroups] = useState([]);
  const [selectedContext, setSelectedContext] = useState("");
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [loadingContext, setLoadingContext] = useState(false);
  const [sending, setSending] = useState(false);
  const [editorReady, setEditorReady] = useState(false);

  const FLAT_MERGE_TAGS = useMemo(
    () => flattenMergeTagsForUnlayer(MERGE_TAGS),
    []
  );

  useEffect(() => {
    async function loadTemplates() {
      try {
        setLoadingTemplates(true);

        const res = await authFetch("/api/templates/email");
        const data = await res.json();
        setTemplates(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load templates:", err);
        setTemplates([]);
      } finally {
        setLoadingTemplates(false);
      }
    }

    loadTemplates();
  }, []);

  useEffect(() => {
    async function loadContext() {
      if (!clientId && !leadId) return;

      try {
        setLoadingContext(true);

        const params = new URLSearchParams();
        if (clientId) params.append("clientId", clientId);
        if (leadId) params.append("leadId", leadId);

        const res = await authFetch(`/api/email-context?${params.toString()}`);
        const data = await res.json();
        setGroups(Array.isArray(data.groups) ? data.groups : []);
      } catch (err) {
        console.error("Failed to load email context:", err);
        setGroups([]);
      } finally {
        setLoadingContext(false);
      }
    }

    loadContext();
  }, [clientId, leadId]);

  const insertAtCursor = (ref, value, setter, closeSetter) => {
    const el = ref.current;
    if (!el) return;

    const start = el.selectionStart;
    const end = el.selectionEnd;

    setter(el.value.slice(0, start) + value + el.value.slice(end));

    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + value.length, start + value.length);
    });

    closeSetter(false);
  };

  const handleEditorReady = (editor) => {
    editor.setMergeTags(FLAT_MERGE_TAGS);
    setEditorReady(true);
  };

  const loadTemplate = async (id) => {
    if (!id || !editorRef.current?.editor) return;

    try {
      const res = await authFetch(`/api/templates/email/${id}`);
      const tpl = await res.json();

      if (tpl.designJson) {
        const parsed =
          typeof tpl.designJson === "string"
            ? JSON.parse(tpl.designJson)
            : tpl.designJson;

        editorRef.current.editor.loadDesign(parsed);
      }

      if (tpl.subject) {
        setSubject(tpl.subject);
      }
    } catch (err) {
      console.error("Failed to load template:", err);
      alert("Failed to load template.");
    }
  };

  const handleSend = () => {
    if (!editorRef.current?.editor) {
      alert("Editor not ready yet.");
      return;
    }

    if (!toEmail.trim()) {
      alert("Recipient email is required.");
      return;
    }

    if (!subject.trim()) {
      alert("Subject is required.");
      return;
    }

    setSending(true);

    editorRef.current.editor.exportHtml(async ({ html }) => {
      try {
        let processedTo = toEmail;

        Object.entries(mergeData || {}).forEach(([key, value]) => {
          const safeValue = value ?? "";
          const regex = new RegExp(`#${key}#`, "g");
          processedTo = processedTo.replace(regex, safeValue);
        });

        const payload = {
          to: processedTo,
          subject,
          html,
          clientId,
          leadId,
          contextId: selectedContext || null,
        };

        const res = await authFetch("/api/emails/send-email", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          const errText = await res.text();
          throw new Error(errText || "Failed to send email");
        }

        alert("Email sent successfully.");
        onClose?.();
      } catch (err) {
        console.error("Email send failed:", err);
        alert(err.message || "Failed to send email");
      } finally {
        setSending(false);
      }
    });
  };

  return (
    <div className="sales-section-card template-editor-main">
      <div className="sales-section-head">
        <div>
          <h2>Email Composer</h2>
          <p>
            Compose and preview an email for {recipientName || recipientEmail || "this record"}.
          </p>
        </div>

        <div className="sales-header-actions">
          <button
            type="button"
            className="sales-secondary-btn"
            onClick={onClose}
          >
            Back to Profile
          </button>

          <button
            type="button"
            className="sales-primary-btn"
            onClick={handleSend}
            disabled={sending || !editorReady}
          >
            {sending ? "Sending..." : "Send Email"}
          </button>
        </div>
      </div>

      <div className="template-editor-fields" style={{ paddingTop: 16 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.1fr 0.9fr",
            gap: 14,
            alignItems: "start",
          }}
        >
          <div className="template-field" style={{ position: "relative" }}>
            <label>To</label>
            <input
              ref={toRef}
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              placeholder="Recipient email"
            />

            <button
              type="button"
              className="template-inline-link"
              onClick={() => setShowToTags((prev) => !prev)}
            >
              Insert merge tag
            </button>

            {showToTags && (
              <div className="template-merge-picker-wrap">
                <MergeTagPicker
                  onInsert={(tag) =>
                    insertAtCursor(toRef, tag, setToEmail, setShowToTags)
                  }
                  onClose={() => setShowToTags(false)}
                />
              </div>
            )}
          </div>

          <div className="template-field">
            <label>Template</label>
            <select
              value={selectedTemplateId}
              onChange={(e) => {
                setSelectedTemplateId(e.target.value);
                loadTemplate(e.target.value);
              }}
              disabled={loadingTemplates}
            >
              <option value="">
                {loadingTemplates ? "Loading templates..." : "Select Template"}
              </option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.1fr 0.9fr",
            gap: 14,
            alignItems: "start",
          }}
        >
          <div className="template-field template-subject-field">
            <label>Subject Line</label>
            <input
              ref={subjectRef}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter subject line"
            />

            <button
              type="button"
              className="template-inline-link"
              onClick={() => setShowSubjectTags((prev) => !prev)}
            >
              Insert merge tag
            </button>

            {showSubjectTags && (
              <div className="template-merge-picker-wrap">
                <MergeTagPicker
                  onInsert={(tag) =>
                    insertAtCursor(subjectRef, tag, setSubject, setShowSubjectTags)
                  }
                  onClose={() => setShowSubjectTags(false)}
                />
              </div>
            )}
          </div>

          <div className="template-field">
            <label>Email Context</label>
            <select
              value={selectedContext}
              onChange={(e) => setSelectedContext(e.target.value)}
              disabled={loadingContext}
            >
              <option value="">
                {loadingContext
                  ? "Loading context..."
                  : "Select Policy / Opportunity"}
              </option>

              {groups.map((group) =>
                group.items?.length ? (
                  <optgroup key={group.key} label={group.label}>
                    {group.items.map((item) => (
                      <option key={item.id} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </optgroup>
                ) : null
              )}
            </select>
          </div>
        </div>
      </div>

      <div className="template-editor-canvas">
        <EmailEditor
          ref={editorRef}
          onReady={handleEditorReady}
          options={UNLAYER_OPTIONS}
          style={{ minHeight: 640 }}
        />
      </div>
    </div>
  );
}

export default EmailComposer;